import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Clock } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { API } from "@/constants/assets";

const inputCls = "field w-full px-4 py-3 text-sm";

export function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("ct_ok"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Erreur");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 md:py-40 bg-[#0B0A08]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-24">
        <div>
          <Reveal><Kicker>{t("ct_kicker")}</Kicker></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif-d text-4xl md:text-5xl mt-6 text-[#F7F4EE]">{t("ct_title")}</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 shrink-0 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]"><MapPin size={16} /></span>
                <div>
                  <div className="text-[10px] font-mono-d tracking-[0.3em] uppercase text-[#7A7367]">Atelier & Showroom</div>
                  <div data-testid="contact-address" className="mt-1 font-serif-d text-xl text-[#F7F4EE]">{t("ct_addr")}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 shrink-0 border hairline flex items-center justify-center text-[#C5A059]"><Phone size={16} /></span>
                <div>
                  <div className="text-[10px] font-mono-d tracking-[0.3em] uppercase text-[#7A7367]">Téléphone</div>
                  <div className="mt-1 font-serif-d text-xl text-[#F7F4EE]">{t("ct_phone")}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 shrink-0 border hairline flex items-center justify-center text-[#C5A059]"><Clock size={16} /></span>
                <div>
                  <div className="text-[10px] font-mono-d tracking-[0.3em] uppercase text-[#7A7367]">Horaires</div>
                  <div className="mt-1 font-serif-d text-xl text-[#F7F4EE]">Lun — Sam · 9h00 — 19h00</div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-12 font-serif-d italic text-lg text-[#C5A059] border-l-2 border-[#C5A059]/40 pl-5">
              « Chaque ouvrage commence par une conversation. »
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form data-testid="contact-form" onSubmit={submit} className="border hairline bg-[#14120F] p-8 md:p-10 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="contact-input-name" required placeholder={t("cfg_name")} value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
              <input data-testid="contact-input-email" required type="email" placeholder={t("cfg_email")} value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
            </div>
            <input data-testid="contact-input-phone" placeholder={t("cfg_phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
            <textarea data-testid="contact-input-message" required rows={5} placeholder={t("cfg_msg")} value={form.message} onChange={(e) => set("message", e.target.value)} className={`${inputCls} resize-none`} />
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="gold-btn w-full border border-[#D4AF37] py-4 text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] disabled:opacity-50"
            >
              {sending ? "…" : t("ct_send")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
